/**
 * Fechas para la interfaz, en Venezuela. Las fechas del dominio son
 * 'YYYY-MM-DD' sin hora (fechas de calendario judicial): NUNCA se pasan por
 * `new Date('YYYY-MM-DD')` (eso es medianoche UTC y en Caracas es el día
 * anterior: memoria `era-marca-y-recortes`). Se construyen a mediodía local.
 */
import { hoyVE, mesVE } from '@era/dominio/calendario';

export { hoyVE, mesVE };

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const MESES_LARGOS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

/** 'YYYY-MM-DD' (o ISO con hora) → Date a mediodía local de ese día de calendario. */
export function aFecha(s: string): Date {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0);
}

export function aISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** «21 oct» (y el año si no es el actual). */
export function fechaCorta(s: string | null | undefined): string {
  if (!s) return '—';
  const f = aFecha(s);
  const anio = f.getFullYear() !== aFecha(hoyVE()).getFullYear() ? ` ${f.getFullYear()}` : '';
  return `${f.getDate()} ${MESES[f.getMonth()]}${anio}`;
}

/** «miércoles 21 de octubre» (y el año si no es el actual). */
export function fechaLarga(s: string | null | undefined): string {
  if (!s) return '—';
  const f = aFecha(s);
  const anio = f.getFullYear() !== aFecha(hoyVE()).getFullYear() ? ` de ${f.getFullYear()}` : '';
  return `${DIAS[f.getDay()]} ${f.getDate()} de ${MESES_LARGOS[f.getMonth()]}${anio}`;
}

/** «jueves 22 de octubre de 2026», siempre con el año: lo que enseña un campo de fecha. */
export function fechaCompleta(s: string | null | undefined): string {
  if (!s) return '—';
  const f = aFecha(s);
  return `${DIAS[f.getDay()]} ${f.getDate()} de ${MESES_LARGOS[f.getMonth()]} de ${f.getFullYear()}`;
}

/** «21/10/2026», para expedientes y documentos. */
export function fechaNumerica(s: string | null | undefined): string {
  if (!s) return '—';
  // Hay campos del servidor que a veces traen texto («admisión de la demanda (…)»,
  // el `desde` de la perención): sin esto salía «NaN/NaN/NaN».
  if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return s;
  const f = aFecha(s);
  return `${String(f.getDate()).padStart(2, '0')}/${String(f.getMonth() + 1).padStart(2, '0')}/${f.getFullYear()}`;
}

export function nombreMes(mes: string): string {
  const [y, m] = mes.split('-').map(Number);
  return `${MESES_LARGOS[m - 1][0].toUpperCase()}${MESES_LARGOS[m - 1].slice(1)} ${y}`;
}

/** Días de calendario de hoy a `s` (negativo = pasado). */
export function diasHasta(s: string): number {
  return Math.round((aFecha(s).getTime() - aFecha(hoyVE()).getTime()) / 86_400_000);
}

/** «Hoy», «Mañana», «En 3 días», «Hace 2 días», o la fecha corta si está lejos. */
export function cuandoRelativo(s: string | null | undefined): string {
  if (!s) return '—';
  const n = diasHasta(s);
  if (n === 0) return 'Hoy';
  if (n === 1) return 'Mañana';
  if (n === -1) return 'Ayer';
  if (n > 1 && n <= 7) return `En ${n} días`;
  if (n < -1 && n >= -7) return `Hace ${-n} días`;
  return fechaCorta(s);
}

/** Importe en bolívares o dólares con separadores venezolanos: «Bs. 12.450,00». */
export function dinero(n: number | string | null | undefined, moneda: 'VES' | 'USD' = 'USD'): string {
  const v = typeof n === 'string' ? Number(n) : n ?? 0;
  const s = new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number.isFinite(v) ? v : 0);
  return moneda === 'VES' ? `Bs. ${s}` : `$ ${s}`;
}
